import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { StatusLine } from "../../components/StatusLine";
import { useIcps } from "../../hooks/useIcps";
import { useIcpSamples } from "../../hooks/useIcpSamples";
import type { Icp } from "../../lib/types";
import { RunDetail } from "./RunDetail";
import { RunStart } from "./RunStart";

function useResolvedIcp(name: string | undefined): {
  icp: Icp | undefined;
  fallback: JSX.Element | null;
} {
  const { data: icps, isLoading } = useIcps({ includeInactive: true });
  const icp = useMemo(
    () => icps?.find((entry) => entry.name === name),
    [icps, name],
  );

  if (isLoading || !icp) {
    return {
      icp: undefined,
      fallback: (
        <section className="card">
          <p className="breadcrumb">
            <Link to="/icps">← All ICPs</Link>
          </p>
          <StatusLine
            variant={isLoading ? "running" : "error"}
            message={isLoading ? "Loading…" : `No ICP named ${name}.`}
          />
        </section>
      ),
    };
  }

  return { icp, fallback: null };
}

export function IcpAuthoringStart() {
  const { name } = useParams<{ name: string }>();
  const { icp, fallback } = useResolvedIcp(name);
  const { data: storedSamples, isLoading: samplesLoading } = useIcpSamples(
    icp?.name ?? null,
  );
  if (!icp) return fallback;
  if (samplesLoading || storedSamples === undefined) {
    return (
      <section className="card">
        <StatusLine variant="running" message="Loading samples…" />
      </section>
    );
  }
  return <RunStart icp={icp} storedSamples={storedSamples} />;
}

export function IcpAuthoringRun() {
  const { name, runId } = useParams<{ name: string; runId: string }>();
  const { icp, fallback } = useResolvedIcp(name);
  if (!icp) return fallback;
  if (!runId) return null;
  return <RunDetail icp={icp} runId={runId} />;
}
